/**
 * Azamphone product page: variant state (<az-product>) and gallery sync
 * (<az-gallery>). Loaded by sections/az-main-product.liquid.
 *
 * Variant rules (design spec section 5): selecting an option updates, together,
 * the selected states, price (with the compare-at price when on sale), gallery
 * image, availability row, the variant id
 * the form submits, the purchase buttons and the mobile purchase bar. Values
 * that would form an unavailable or missing combination are marked but stay
 * selectable; choosing them yields the disabled purchase state.
 * The URL's ?variant= is kept in sync (history.replaceState) so a language
 * switch can keep the selected variant.
 *
 * Lifecycle: custom elements, so Theme Editor re-rendering is handled by
 * connected/disconnectedCallback; definitions are guarded.
 */
(() => {
  /** Same output as snippets/az-money.liquid: EN "AED 1,234", RU "1 234 AED". */
  function formatMoney(cents, locale, currency) {
    const whole = Math.floor(cents / 100);
    const fraction = cents % 100;
    const isRu = locale === 'ru';
    const group = isRu ? ' ' : ',';
    const decimal = isRu ? ',' : '.';
    let amount = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, group);
    if (fraction > 0) amount += decimal + String(fraction).padStart(2, '0');
    return isRu ? `${amount} ${currency}` : `${currency} ${amount}`;
  }

  class AzGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
      this.current = this.querySelector('[data-gallery-current]');

      this.handleThumb = (event) => {
        const thumb = event.target.closest('[data-gallery-thumb]');
        if (!thumb) return;
        event.preventDefault();
        this.showMedia(thumb.dataset.mediaId);
      };
      this.handleScroll = () => {
        window.cancelAnimationFrame(this.frame);
        this.frame = window.requestAnimationFrame(() => this.syncActive());
      };
      this.addEventListener('click', this.handleThumb);
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });

      /* Start on the selected variant's image (e.g. after ?variant=). */
      const initial = this.dataset.initialMedia;
      if (initial && this.slides.length > 1 && this.slides[0].dataset.mediaId !== initial) {
        const slide = this.slides.find((item) => item.dataset.mediaId === initial);
        if (slide) {
          this.track.scrollLeft = slide.offsetLeft;
          this.setActive(this.slides.indexOf(slide));
        }
      }
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.removeEventListener('click', this.handleThumb);
      this.track.removeEventListener('scroll', this.handleScroll);
      window.cancelAnimationFrame(this.frame);
    }

    /** Scroll the stage to a media id (used by thumbnails and variant changes). */
    showMedia(mediaId) {
      if (!this.track || mediaId == null) return;
      const slide = this.slides.find((item) => item.dataset.mediaId === String(mediaId));
      if (!slide) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      /* The track is position: relative, so offsetLeft is measured from it. */
      this.track.scrollTo({ left: slide.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
      this.setActive(this.slides.indexOf(slide));
    }

    syncActive() {
      const left = this.track.scrollLeft;
      let index = 0;
      let best = Infinity;
      this.slides.forEach((slide, i) => {
        const distance = Math.abs(slide.offsetLeft - left);
        if (distance < best) {
          best = distance;
          index = i;
        }
      });
      this.setActive(index);
    }

    setActive(index) {
      this.thumbs.forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      if (this.current) this.current.textContent = String(index + 1);
    }
  }

  class AzProduct extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-variants]');
      this.picker = this.querySelector('[data-variant-picker]');
      if (!data || !this.picker) return;
      try {
        this.variants = JSON.parse(data.textContent);
      } catch (error) {
        return;
      }
      this.locale = this.dataset.moneyLocale;
      this.currency = this.dataset.currency;
      this.fieldsets = Array.from(this.picker.querySelectorAll('[data-option-position]'));

      /* JavaScript is running: the radios drive the hidden variant id. */
      const idInput = this.querySelector('[data-variant-id]');
      if (idInput) idInput.disabled = false;

      this.handleChange = (event) => {
        if (event.target.matches('[data-option-value]')) this.update();
      };
      this.picker.addEventListener('change', this.handleChange);
    }

    disconnectedCallback() {
      if (this.picker && this.handleChange) this.picker.removeEventListener('change', this.handleChange);
    }

    selectedOptions() {
      return this.fieldsets.map((fieldset) => {
        const checked = fieldset.querySelector('[data-option-value]:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant(options) {
      return this.variants.find((variant) => variant.options.every((value, i) => value === options[i]));
    }

    update() {
      const options = this.selectedOptions();
      const variant = this.findVariant(options);
      const available = Boolean(variant && variant.available);

      /* Selected labels ("Colour · Silver"). */
      this.fieldsets.forEach((fieldset, i) => {
        const label = fieldset.querySelector('[data-selected-value]');
        if (label) label.textContent = options[i] || '';
      });

      /* Availability of every option value, given the other selections. */
      this.fieldsets.forEach((fieldset, position) => {
        fieldset.querySelectorAll('[data-option-value]').forEach((input) => {
          const candidate = options.slice();
          candidate[position] = input.value;
          const match = this.findVariant(candidate);
          const ok = Boolean(match && match.available);
          const label = this.querySelector(`label[for="${CSS.escape(input.id)}"]`);
          if (!label) return;
          label.classList.toggle('is-unavailable', !ok);
          const note = label.querySelector('[data-unavailable-note]');
          if (note) note.hidden = ok;
          const sub = label.querySelector('[data-pill-sub]');
          if (sub) {
            sub.textContent = ok ? formatMoney(match.price, this.locale, this.currency) : this.dataset.textUnavailable;
          }
        });
      });

      /* Variant id, price, availability, buttons, purchase bar. */
      const idInput = this.querySelector('[data-variant-id]');
      if (idInput && variant) idInput.value = variant.id;

      const priceText = variant ? formatMoney(variant.price, this.locale, this.currency) : '';
      this.querySelectorAll('[data-price], [data-bar-price]').forEach((node) => {
        if (variant) node.textContent = priceText;
      });
      /* Compare-at price: shown struck through only when the variant is on sale. */
      const compare = this.querySelector('[data-compare-price]');
      if (compare && variant) {
        const onSale = variant.compare_at_price > variant.price;
        compare.hidden = !onSale;
        const amount = compare.querySelector('[data-compare-amount]');
        if (amount && onSale) amount.textContent = formatMoney(variant.compare_at_price, this.locale, this.currency);
      }
      const barVariant = this.querySelector('[data-bar-variant]');
      if (barVariant) barVariant.textContent = variant ? variant.title : options.join(' / ');

      const availability = this.querySelector('[data-availability]');
      if (availability) {
        availability.classList.toggle('is-available', available);
        const text = availability.querySelector('[data-availability-text]');
        if (text) text.textContent = available ? this.dataset.textInStock : this.dataset.textUnavailable;
      }

      this.querySelectorAll('[data-add-button]').forEach((button) => {
        button.disabled = !available;
        const label = button.querySelector('[data-add-label]');
        if (label) label.textContent = available ? this.dataset.textAdd : this.dataset.textUnavailable;
      });
      const buyNow = this.querySelector('[data-buy-now]');
      if (buyNow) buyNow.hidden = !available;

      /* WhatsApp link: message with the selected variant and its URL. */
      this.querySelectorAll('[data-wa-template]').forEach((link) => {
        if (!variant) return;
        const variantUrl = `${window.location.origin}${this.dataset.productUrl}?variant=${variant.id}`;
        /* The template comes from an HTML-escaped translation ("I&#39;m"):
         * decode the entities so WhatsApp receives plain text. */
        const decoder = document.createElement('textarea');
        decoder.innerHTML = link.dataset.waTemplate;
        const message = decoder.value
          .replace('__VARIANT__', variant.title)
          .replace('__URL__', variantUrl);
        link.href = link.dataset.waBase + encodeURIComponent(message);
      });

      /* Gallery image for the variant. */
      if (variant && variant.media) {
        const gallery = this.querySelector('az-gallery');
        if (gallery && typeof gallery.showMedia === 'function') gallery.showMedia(variant.media);
      }

      /* Keep ?variant= in the URL (language switch keeps the selection). */
      if (variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url.toString());
      }
    }
  }

  if (!customElements.get('az-gallery')) customElements.define('az-gallery', AzGallery);
  if (!customElements.get('az-product')) customElements.define('az-product', AzProduct);
})();
